<template>
  <div class="p-6 max-w-5xl mx-auto">
    <el-card class="rounded-2xl shadow-md">
      <h2 class="text-xl font-bold mb-4">编辑植物</h2>

      <el-form
          ref="formRef"
          :model="plantForm"
          :rules="rules"
          label-width="120px"
          class="space-y-4"
      >
        <!-- 植物名称 -->
        <el-form-item label="植物名称" prop="name">
          <el-input v-model="plantForm.name" placeholder="请输入植物名称" />
        </el-form-item>

        <!-- 拉丁名 -->
        <el-form-item label="拉丁名" prop="latinName">
          <el-input v-model="plantForm.latinName" placeholder="请输入拉丁名" />
        </el-form-item>

        <el-form-item label="花语">
          <el-input v-model="plantForm.passionate_love" placeholder="请输入花语" />
        </el-form-item>

        <!-- 科属 -->
        <el-form-item label="科属" prop="family">
          <el-input v-model="plantForm.family" placeholder="请输入科属" />
        </el-form-item>

        <!-- 简介 -->
        <el-form-item label="简介" prop="intro">
          <el-input
              type="textarea"
              v-model="plantForm.intro"
              placeholder="请输入简介"
              :rows="3"
          />
        </el-form-item>

        <el-form-item label="热门植物">
          <el-radio-group v-model="plantForm.hot_plants">
            <el-radio :label="true">是</el-radio>
            <el-radio :label="false">否</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="展示花语祈福">
          <el-radio-group v-model="plantForm.is_show">
            <el-radio :label="true">是</el-radio>
            <el-radio :label="false">否</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 图片上传 -->
        <el-form-item label="上传图片" prop="imageURL">
          <div class="upload-field">
            <el-upload
                class="upload-box"
                :action="uploadImageApi"
                :on-success="handleUploadSuccess"
                :on-error="handleUploadError"
                :limit="1"
                :show-file-list="false"
            >
              <el-button type="primary">选择图片</el-button>
            </el-upload>
            <div v-if="plantForm.imageURL" class="preview-thumb mt-2">
              <img :src="plantForm.imageURL" alt="预览" class="w-40 h-40 object-cover rounded-xl border" />
            </div>
          </div>
        </el-form-item>

        <!-- 栽培信息 -->
        <el-divider>栽培信息</el-divider>
        <el-form-item label="土壤" prop="soil">
          <el-input v-model="plantForm.soil" />
        </el-form-item>
        <el-form-item label="温度" prop="temperature">
          <el-input v-model="plantForm.temperature" />
        </el-form-item>
        <el-form-item label="光照" prop="light">
          <el-input v-model="plantForm.light" />
        </el-form-item>
        <el-form-item label="浇水" prop="watering">
          <el-input v-model="plantForm.watering" />
        </el-form-item>
        <el-form-item label="修剪" prop="pruning">
          <el-input v-model="plantForm.pruning" />
        </el-form-item>

        <!-- 繁殖方式 -->
        <el-form-item label="繁殖方式" prop="propagation">
          <el-input v-model="plantForm.propagation" />
        </el-form-item>

        <div class="flex justify-end space-x-4 mt-6">
          <el-button @click="resetForm">重置</el-button>
          <el-button type="primary" :loading="submitting" @click="submitForm">
            更新植物
          </el-button>
        </div>
      </el-form>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import api, { API_BASE_URL } from '@/services/api'

const route = useRoute()
const router = useRouter()
const formRef = ref(null)
const submitting = ref(false)

const plantId = route.params.id

// 表单模型
const plantForm = reactive({
  id: plantId,
  name: '',
  latinName: '',
  passionate_love: '',
  family: '',
  intro: '',
  hot_plants: false,
  is_show: false,
  imageURL: '',
  soil: '',
  temperature: '',
  light: '',
  watering: '',
  pruning: '',
  propagation: ''
})

// 校验规则
const rules = {
  name: [{ required: true, message: '请输入植物名称', trigger: 'blur' }],
  imageURL: [{ required: true, message: '请上传植物图片', trigger: 'blur' }]
}

// 图片上传地址
const uploadImageApi = computed(() => {
  if (!plantForm.id) return ''
  const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL
  return `${baseUrl}/upload/image/${plantForm.id}`
})

// 获取植物详情
const fetchPlantDetail = async () => {
  try {
    const res = await api.get(`/plants/${plantId}`)
    const p = res.data

    plantForm.id = p.id
    plantForm.name = p.name || ''
    plantForm.latinName = p.latin_name || ''
    plantForm.passionate_love = p.passionate_love || ''
    plantForm.family = p.family || ''
    plantForm.intro = p.intro || ''
    plantForm.hot_plants = p.hot_plants || false
    plantForm.is_show = p.is_show || false
    plantForm.imageURL = p.image || ''
    plantForm.soil = p.soil || ''
    plantForm.temperature = p.temperature || ''
    plantForm.light = p.light || ''
    plantForm.watering = p.watering || ''
    plantForm.pruning = p.pruning || ''
    plantForm.propagation = p.propagation || ''

  } catch (err) {
    console.error(err)
    ElMessage.error('加载植物详情失败')
  }
}

// 上传图片
const handleUploadSuccess = (res) => {
  if (res.url) {
    plantForm.imageURL = res.url
    ElMessage.success('图片上传成功')
  } else {
    ElMessage.error('上传返回格式不正确')
  }
}

const handleUploadError = () => ElMessage.error('图片上传失败')

// 提交更新
const submitForm = () => {
  formRef.value.validate(async (valid) => {
    if (!valid) return
    submitting.value = true
    try {
      await api.put(`/plants/update/${plantForm.id}`, plantForm)
      ElMessage.success('植物信息更新成功')
      router.push('/baike')
    } catch (err) {
      console.error(err)
      ElMessage.error('更新失败')
    } finally {
      submitting.value = false
    }
  })
}

// 重置表单
const resetForm = () => fetchPlantDetail()

onMounted(() => {
  fetchPlantDetail()
})
</script>

<style scoped>
.upload-field {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.preview-thumb img {
  width: 160px;
  height: 160px;
  border-radius: 12px;
  object-fit: cover;
  border: 1px solid #ddd;
}
</style>
